import { z } from 'zod';

export const registerSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  countryCode: z.string().min(2, 'Please select a country').max(2, 'Invalid country code'),
  phone: z.string().regex(/^\+[1-9]\d{1,14}$/, 'Phone must be in E.164 format (e.g., +1234567890)').optional().or(z.literal('')),
  affiliateCode: z.string().optional(),
});

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export const createRaffleSchema = z.object({
  name: z.string().min(10, 'Name must be at least 10 characters').max(250, 'Name must not exceed 250 characters'),
  description: z.string().min(50, 'Description must be at least 50 characters').max(5000, 'Description must not exceed 5000 characters'),
  ticketPrice: z.number().min(1, 'Ticket price must be at least 1'),
  currency: z.string().min(1, 'Currency is required'),
  salesStartDate: z.string().min(1, 'Sales start date is required'),
  salesEndDate: z.string().min(1, 'Sales end date is required'),
  drawSchedule: z.enum(['ONE_TIME', 'DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY']),
  numberOfWinners: z.number().min(1, 'At least 1 winner is required'),
  prizeDistribution: z.string().min(1, 'Prize distribution is required'),
  organizerShare: z.number().min(0).max(100, 'Organizer share must be between 0 and 100'),
  participationAccess: z.enum(['OPEN', 'INVITE_EMAIL', 'INVITE_CODE']),
  imageUrl: z.string().optional(),
}).refine((data) => {
  // Validate prize distribution count matches number of winners
  const prizes = data.prizeDistribution.split(',').map(p => p.trim()).filter(p => p);
  if (prizes.length !== data.numberOfWinners) {
    return false;
  }
  
  // Validate all prizes are valid numbers
  const prizeNumbers = prizes.map(p => parseFloat(p));
  if (prizeNumbers.some(p => isNaN(p) || p <= 0)) {
    return false;
  }
  
  // Validate total percentages
  const totalPrizes = prizeNumbers.reduce((sum, p) => sum + p, 0);
  const total = totalPrizes + data.organizerShare;
  
  // Allow some tolerance for floating point math, and assume platform fee will be added by backend
  // Total should be <= 100 (platform fee will make it exactly 100)
  if (total > 100) {
    return false;
  }
  
  return true;
}, {
  message: 'Prize distribution must match number of winners, contain valid percentages, and total with organizer share must not exceed 100%',
  path: ['prizeDistribution'],
});

export const purchaseTicketSchema = z.object({
  raffleId: z.string().min(1, 'Raffle ID is required'),
  quantity: z.number().min(1, 'Quantity must be at least 1'),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),
  accessCode: z.string().optional(),
  paymentProvider: z.enum(['PAYSTACK', 'STRIPE', 'PAYPAL', 'WALLET']),
});

export const bankPayoutSchema = z.object({
  method: z.literal('BANK_TRANSFER'),
  accountName: z.string().min(1, 'Account name is required'),
  accountNumber: z.string().min(1, 'Account number is required'),
  bankName: z.string().min(1, 'Bank name is required'),
  bankCode: z.string().min(1, 'Bank code is required'),
  country: z.string().min(1, 'Country is required'),
});

export const paypalPayoutSchema = z.object({
  method: z.literal('PAYPAL'),
  paypalEmail: z.string().email('Invalid PayPal email address'),
  country: z.string().min(1, 'Country is required'),
});

export const withdrawalSchema = z.object({
  amount: z.number().min(1, 'Amount must be greater than 0'),
  payoutSettingId: z.string().min(1, 'Please select a payout method'),
});

export const sendInvitationsSchema = z.object({
  raffleId: z.string().min(1, 'Raffle ID is required'),
  emails: z.string().min(1, 'At least one email is required'),
});

export const addCodeRaffleSchema = z.object({
  raffleId: z.string().min(1, 'Raffle ID is required'),
  accessCode: z.string().min(1, 'Access code is required'),
});
